import {
  Steps,
  StepsItemContext,
  type StepsItemProps,
  type StepsListProps,
  type StepsRootProps,
  type StepsSeparatorProps,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import { Text } from "../text/Text.js";

type StepperSizeProps = "sm" | "md" | "lg";
type StepperVariantProps = "filled" | "outlined";
const defaultStepsSize: StepperSizeProps = "md";
const defaultStepsVariant: StepperVariantProps = "filled";

interface StepperProps extends StepsRootProps {
  size?: StepperSizeProps;
  variant?: StepperVariantProps;
}

interface StepperItemProps extends StepsItemProps {
  label: string;
  indicator?: React.ReactNode;
  completedIndicator?: React.ReactNode;
  hasError?: boolean;
  description?: string;
  separatorProps?: StepsSeparatorProps;
  classNames?: {
    root?: string;
    trigger?: string;
    separator?: string;
    indicator?: string;
    label?: string;
  };
}

const StepsContext = React.createContext<{
  size: StepperSizeProps;
  variant: StepperVariantProps;
}>({
  size: defaultStepsSize,
  variant: defaultStepsVariant,
});

const useStepperContext = () => {
  const context = React.useContext(StepsContext);
  if (!context) {
    throw new Error("StepsContext must be used within a StepsProvider");
  }
  return context;
};

const Stepper = React.forwardRef<
  React.ElementRef<typeof Steps.Root>,
  StepperProps
>((props, ref) => {
  const {
    className,
    size = defaultStepsSize,
    variant = defaultStepsVariant,
    orientation = "horizontal",
    ...rest
  } = props;
  return (
    <StepsContext.Provider value={{ size, variant }}>
      <Steps.Root
        className={clsx("ox-reset ox-StepperRoot", className)}
        ref={ref}
        data-size={size}
        orientation={orientation}
        {...rest}
      />
    </StepsContext.Provider>
  );
});

const StepperList = React.forwardRef<
  React.ElementRef<typeof Steps.List>,
  StepsListProps
>((props, ref) => {
  const { className, ...rest } = props;
  const { size } = useStepperContext();
  return (
    <Steps.List
      data-size={size}
      className={clsx("ox-reset ox-StepperList", className)}
      {...rest}
      ref={ref}
    />
  );
});

const StepperItem = React.forwardRef<
  React.ElementRef<typeof Steps.Item>,
  StepperItemProps
>((props, ref) => {
  const {
    className,
    classNames,
    label,
    index,
    hasError,
    description,
    indicator = index + 1,
    completedIndicator,
    separatorProps,
    ...rest
  } = props;
  const { size, variant } = useStepperContext();
  return (
    <Steps.Item
      index={index}
      data-size={size}
      data-error={hasError}
      className={clsx("ox-reset ox-StepperItem", className, classNames?.root)}
      {...rest}
      ref={ref}
    >
      <Steps.Trigger
        className={clsx("ox-reset ox-StepperTrigger", classNames?.trigger)}
        data-variant={variant}
        data-size={size}
      >
        <Steps.Indicator
          data-variant={variant}
          className={clsx(
            "ox-reset ox-StepperIndicator",
            classNames?.indicator,
          )}
        >
          <StepsItemContext>
            {({ completed }) =>
              hasError ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 256 256"
                  fill="currentColor"
                >
                  <path d="M205.66 194.34a8 8 0 01-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 01-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0111.32-11.32L128 116.69l66.34-66.35a8 8 0 0111.32 11.32L139.31 128z"></path>
                </svg>
              ) : completedIndicator ? (
                completed ? (
                  completedIndicator
                ) : (
                  indicator
                )
              ) : (
                indicator
              )
            }
          </StepsItemContext>
        </Steps.Indicator>
        <span className={clsx("ox-reset ox-StepperLabel", classNames?.label)}>
          {label}
        </span>
      </Steps.Trigger>
      <Steps.Separator
        className={clsx("ox-reset ox-StepperSeparator", classNames?.separator)}
        {...separatorProps}
      />
      {description ? (
        <Text size="md" fontWeight="regular" className="ox-StepperDescription">
          {description}
        </Text>
      ) : null}
    </Steps.Item>
  );
});

const StepperContent = Steps.Content;
const StepperPrevTrigger = Steps.PrevTrigger;
const StepperNextTrigger = Steps.NextTrigger;
const StepperCompletedContent = Steps.CompletedContent;
const StepperContext = Steps.Context;

Stepper.displayName = "Stepper";
StepperList.displayName = "StepperList";
StepperItem.displayName = "StepperItem";
StepperContent.displayName = "StepperContent";
StepperPrevTrigger.displayName = "StepperPrevTrigger";
StepperNextTrigger.displayName = "StepperNextTrigger";
StepperCompletedContent.displayName = "StepperCompletedContent";
StepperContext.displayName = "StepperContext";

export {
  Stepper,
  StepperList,
  StepperItem,
  StepperContent,
  StepperPrevTrigger,
  StepperNextTrigger,
  StepperCompletedContent,
  StepperContext,
};
export type { StepperProps, StepperItemProps, StepperSizeProps };
